import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, ExternalLink } from 'lucide-react';
import TopbarView from './TopbarView';
import logo from '../assets/logo/VJ-College-logo.png';
import ihs from '../assets/logo/ihs.png';
import './Navbar.css';

export default function NavbarView() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const location = useLocation();

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
    setActiveAccordion(null);
  }, [location.pathname]);

  const toggleAccordion = (key) => {
    setActiveAccordion(activeAccordion === key ? null : key);
  };

  return (
    <header className="vj-header-wrapper">
      {/* Top Institutional Info Ribbon */}
      <TopbarView />

      {/* Main Branding Strip - Center Aligned */}
      <div className="vj-header-brand position-relative">
        <div className="container-fluid px-3 px-lg-4">
          <div className="d-flex justify-content-center align-items-center">
            {/* Center Brand Unit: Logo -> College Name & Subtitles -> IHS Crest */}
            <Link to="/" className="vj-brand-center-unit text-decoration-none">
              {/* College Logo */}
              <div className="vj-brand-logo-container flex-shrink-0">
                <img
                  src={logo}
                  alt="Vidyajyoti College Logo"
                  className="vj-main-logo img-fluid"
                />
              </div>

              {/* College Name, Title & Subtitle */}
              <div className="text-center px-1 px-md-3">
                {/* Heritage Ribbon */}
                <div className="vj-brand-est-ribbon">
                  <span className="vj-est-rule"></span>
                  <span className="vj-est-badge">ESTD. 1879 • PONTIFICAL FACULTY</span>
                  <span className="vj-est-rule"></span>
                </div>

                <h1 className="vj-brand-title mb-1">
                  <span className="vj-brand-hero">VIDYAJYOTI</span>
                  <span className="vj-brand-rest"> COLLEGE OF THEOLOGY</span>
                </h1>

                <div className="vj-brand-subtitles-group">
                  <span className="vj-brand-subtitle">FACULTY OF THEOLOGY &amp; INSTITUTE OF RELIGIOUS STUDIES • DELHI</span>
                </div>
              </div>

              {/* Jesuit IHS Crest */}
              <div className="vj-ihs-container flex-shrink-0 d-none d-sm-block">
                <img
                  src={ihs}
                  alt="Society of Jesus (IHS)"
                  className="vj-ihs-logo img-fluid"
                />
              </div>
            </Link>

            {/* Mobile Menu Toggle Button */}
            <div className="position-absolute end-0 top-50 translate-middle-y me-3 d-lg-none">
              <button
                type="button"
                className="btn vj-mobile-toggle p-2 rounded-3 shadow-sm"
                onClick={() => setMobileOpen(true)}
                aria-label="Toggle navigation menu"
              >
                <Menu size={22} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Sticky Desktop Navigation Bar */}
      <nav className="vj-navbar-sticky d-none d-lg-block">
        <div className="container-fluid px-lg-4">
          <div className="d-flex align-items-center justify-content-center flex-wrap gap-1 py-1">
            <Link to="/" className={`vj-nav-link ${location.pathname === '/' ? 'active' : ''}`}>
              Home
            </Link>

            {/* About Dropdown */}
            <div className="vj-dropdown">
              <span className="vj-nav-link cursor-pointer">
                About <ChevronDown size={14} />
              </span>
              <div className="vj-dropdown-menu">
                <Link to="/about/vision-mission" className="vj-dropdown-item">Vision & Mission</Link>
                <Link to="/about/history" className="vj-dropdown-item">History of VJ</Link>
                <Link to="/about/Officials" className="vj-dropdown-item">Academic Authorities</Link>
                <Link to="/about/principalmessage" className="vj-dropdown-item">Principal's Message</Link>
                <Link to="/about/formation" className="vj-dropdown-item">Formation</Link>
                <Link to="/about/sjinfo" className="vj-dropdown-item">Society of Jesus</Link>
              </div>
            </div>

            {/* Faculty Dropdown */}
            <div className="vj-dropdown">
              <span className="vj-nav-link cursor-pointer">
                Faculty <ChevronDown size={14} />
              </span>
              <div className="vj-dropdown-menu">
                <Link to="/about/residentialFaculty" className="vj-dropdown-item">Residential Faculty</Link>
                <Link to="/about/visitingFaculty" className="vj-dropdown-item">Visiting Faculty</Link>
              </div>
            </div>

            {/* Academics Dropdown */}
            <div className="vj-dropdown">
              <span className="vj-nav-link cursor-pointer">
                Academics <ChevronDown size={14} />
              </span>
              <div className="vj-dropdown-menu">
                <Link to="/academics/methodolgoy" className="vj-dropdown-item">Methodology</Link>
                <Link to="/academics/bachealor" className="vj-dropdown-item">Bachelors (B.Th)</Link>
                <Link to="/academics/master" className="vj-dropdown-item">Master (M.Th)</Link>
                <Link to="/academics/doctoral" className="vj-dropdown-item">Doctoral Studies</Link>
                <Link to="/academics/diploma" className="vj-dropdown-item">Diploma Program</Link>
                <Link to="/academics/certificate" className="vj-dropdown-item">Certificate Program</Link>
                <Link to="/academics/depth" className="vj-dropdown-item">DEPTh (Distance)</Link>
              </div>
            </div>

            {/* Students Dropdown */}
            <div className="vj-dropdown">
              <span className="vj-nav-link cursor-pointer">
                Students <ChevronDown size={14} />
              </span>
              <div className="vj-dropdown-menu">
                <a href="https://students.vidyajyoticollege.in" target="_blank" rel="noreferrer" className="vj-dropdown-item d-flex justify-content-between align-items-center">
                  <span>Student Portal</span> <ExternalLink size={12} />
                </a>
                <Link to="/students/fieldwork" className="vj-dropdown-item">Field Work</Link>
              </div>
            </div>

            {/* Admission Dropdown */}
            <div className="vj-dropdown">
              <span className="vj-nav-link cursor-pointer">
                Admission <ChevronDown size={14} />
              </span>
              <div className="vj-dropdown-menu">
                <Link to="/admission/details" className="vj-dropdown-item">Admission Details</Link>
                <Link to="/admission/application-form" className="vj-dropdown-item">Online Application</Link>
                <Link to="/admission/application-status" className="vj-dropdown-item">Application Status</Link>
              </div>
            </div>

            {/* Library Dropdown */}
            <div className="vj-dropdown">
              <span className="vj-nav-link cursor-pointer">
                Library <ChevronDown size={14} />
              </span>
              <div className="vj-dropdown-menu">
                <a href="https://library.vidyajyoticollege.in" target="_blank" rel="noreferrer" className="vj-dropdown-item d-flex justify-content-between align-items-center">
                  <span>Library Portal</span> <ExternalLink size={12} />
                </a>
                <Link to="/library/about" className="vj-dropdown-item">About Library</Link>
                <Link to="/library/latest" className="vj-dropdown-item">Latest Arrivals</Link>
              </div>
            </div>

            {/* Gallery Dropdown */}
            <div className="vj-dropdown">
              <span className="vj-nav-link cursor-pointer">
                Gallery <ChevronDown size={14} />
              </span>
              <div className="vj-dropdown-menu">
                <Link to="/gallery/photo" className="vj-dropdown-item">Photos</Link>
                <Link to="/gallery/video" className="vj-dropdown-item">Videos</Link>
              </div>
            </div>

            {/* Services Dropdown */}
            <div className="vj-dropdown">
              <span className="vj-nav-link cursor-pointer">
                Services <ChevronDown size={14} />
              </span>
              <div className="vj-dropdown-menu">
                <Link to="/services/accomodation" className="vj-dropdown-item">Accomodation</Link>
                <a href="https://adminoffice.vidyajyoticollege.in" target="_blank" rel="noreferrer" className="vj-dropdown-item d-flex justify-content-between align-items-center">
                  <span>Admin Office</span> <ExternalLink size={12} />
                </a>
              </div>
            </div>

            {/* Publications Dropdown */}
            <div className="vj-dropdown">
              <span className="vj-nav-link cursor-pointer">
                Publications <ChevronDown size={14} />
              </span>
              <div className="vj-dropdown-menu">
                <Link to="/publications/youtube" className="vj-dropdown-item">YouTube Channel</Link>
                <Link to="/publications/vj-journal" className="vj-dropdown-item">Vidyajyoti Journal (VJTR)</Link>
                <Link to="/publications/ace" className="vj-dropdown-item">AVE Reflections</Link>
                <Link to="/publications/vjtoday" className="vj-dropdown-item">VJ Today</Link>
              </div>
            </div>

            <Link to="/academics/depth" className={`vj-nav-link ${location.pathname === '/academics/depth' ? 'active' : ''}`}>
              DEPTh
            </Link>

            <Link to="/contact" className={`vj-nav-link ${location.pathname === '/contact' ? 'active' : ''}`}>
              Contact
            </Link>
          </div>
        </div>
      </nav>

      {/* Mobile Offcanvas Drawer */}
      {mobileOpen && (
        <div className="vj-mobile-drawer">
          <div className="vj-drawer-backdrop" onClick={() => setMobileOpen(false)} />
          <div className="vj-drawer-panel">
            <div className="vj-drawer-header">
              <div className="d-flex align-items-center gap-2">
                <img src={logo} alt="Logo" style={{ width: '36px', height: '36px' }} />
                <span className="fw-bold fs-6">Vidyajyoti College</span>
              </div>
              <button
                type="button"
                className="btn btn-sm text-white p-0"
                onClick={() => setMobileOpen(false)}
              >
                <X size={22} />
              </button>
            </div>

            <div className="vj-drawer-body">
              <Link to="/" className="vj-mobile-link">Home</Link>

              {/* About Accordion */}
              <div>
                <div className="vj-mobile-link cursor-pointer" onClick={() => toggleAccordion('about')}>
                  <span>About</span>
                  <ChevronDown size={16} className={activeAccordion === 'about' ? 'rotate-180' : ''} />
                </div>
                {activeAccordion === 'about' && (
                  <div className="vj-mobile-submenu">
                    <Link to="/about/vision-mission" className="vj-mobile-sublink">Vision & Mission</Link>
                    <Link to="/about/history" className="vj-mobile-sublink">History of VJ</Link>
                    <Link to="/about/Officials" className="vj-mobile-sublink">Academic Authorities</Link>
                    <Link to="/about/principalmessage" className="vj-mobile-sublink">Principal's Message</Link>
                    <Link to="/about/formation" className="vj-mobile-sublink">Formation</Link>
                    <Link to="/about/sjinfo" className="vj-mobile-sublink">Society of Jesus</Link>
                  </div>
                )}
              </div>

              {/* Faculty Accordion */}
              <div>
                <div className="vj-mobile-link cursor-pointer" onClick={() => toggleAccordion('faculty')}>
                  <span>Faculty</span>
                  <ChevronDown size={16} className={activeAccordion === 'faculty' ? 'rotate-180' : ''} />
                </div>
                {activeAccordion === 'faculty' && (
                  <div className="vj-mobile-submenu">
                    <Link to="/about/residentialFaculty" className="vj-mobile-sublink">Residential Faculty</Link>
                    <Link to="/about/visitingFaculty" className="vj-mobile-sublink">Visiting Faculty</Link>
                  </div>
                )}
              </div>

              {/* Academics Accordion */}
              <div>
                <div className="vj-mobile-link cursor-pointer" onClick={() => toggleAccordion('academics')}>
                  <span>Academics</span>
                  <ChevronDown size={16} className={activeAccordion === 'academics' ? 'rotate-180' : ''} />
                </div>
                {activeAccordion === 'academics' && (
                  <div className="vj-mobile-submenu">
                    <Link to="/academics/methodolgoy" className="vj-mobile-sublink">Methodology</Link>
                    <Link to="/academics/bachealor" className="vj-mobile-sublink">Bachelors (B.Th)</Link>
                    <Link to="/academics/master" className="vj-mobile-sublink">Master (M.Th)</Link>
                    <Link to="/academics/doctoral" className="vj-mobile-sublink">Doctoral Studies</Link>
                    <Link to="/academics/diploma" className="vj-mobile-sublink">Diploma Program</Link>
                    <Link to="/academics/certificate" className="vj-mobile-sublink">Certificate Program</Link>
                    <Link to="/academics/depth" className="vj-mobile-sublink">DEPTh (Distance)</Link>
                  </div>
                )}
              </div>

              {/* Students Accordion */}
              <div>
                <div className="vj-mobile-link cursor-pointer" onClick={() => toggleAccordion('students')}>
                  <span>Students</span>
                  <ChevronDown size={16} className={activeAccordion === 'students' ? 'rotate-180' : ''} />
                </div>
                {activeAccordion === 'students' && (
                  <div className="vj-mobile-submenu">
                    <a
                      href="https://students.vidyajyoticollege.in"
                      target="_blank"
                      rel="noreferrer"
                      className="vj-mobile-sublink d-flex justify-content-between align-items-center"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span>Student Portal</span> <ExternalLink size={12} />
                    </a>
                    <Link to="/students/fieldwork" className="vj-mobile-sublink">Field Work</Link>
                  </div>
                )}
              </div>

              {/* Admission Accordion */}
              <div>
                <div className="vj-mobile-link cursor-pointer" onClick={() => toggleAccordion('admission')}>
                  <span>Admission</span>
                  <ChevronDown size={16} className={activeAccordion === 'admission' ? 'rotate-180' : ''} />
                </div>
                {activeAccordion === 'admission' && (
                  <div className="vj-mobile-submenu">
                    <Link to="/admission/details" className="vj-mobile-sublink">Admission Details</Link>
                    <Link to="/admission/application-form" className="vj-mobile-sublink">Online Application</Link>
                    <Link to="/admission/application-status" className="vj-mobile-sublink">Application Status</Link>
                  </div>
                )}
              </div>

              {/* Library Accordion */}
              <div>
                <div className="vj-mobile-link cursor-pointer" onClick={() => toggleAccordion('library')}>
                  <span>Library</span>
                  <ChevronDown size={16} className={activeAccordion === 'library' ? 'rotate-180' : ''} />
                </div>
                {activeAccordion === 'library' && (
                  <div className="vj-mobile-submenu">
                    <a
                      href="https://library.vidyajyoticollege.in"
                      target="_blank"
                      rel="noreferrer"
                      className="vj-mobile-sublink d-flex justify-content-between align-items-center"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span>Library Portal</span> <ExternalLink size={12} />
                    </a>
                    <Link to="/library/about" className="vj-mobile-sublink">About Library</Link>
                    <Link to="/library/latest" className="vj-mobile-sublink">Latest Arrivals</Link>
                  </div>
                )}
              </div>

              {/* Gallery Accordion */}
              <div>
                <div className="vj-mobile-link cursor-pointer" onClick={() => toggleAccordion('gallery')}>
                  <span>Gallery</span>
                  <ChevronDown size={16} className={activeAccordion === 'gallery' ? 'rotate-180' : ''} />
                </div>
                {activeAccordion === 'gallery' && (
                  <div className="vj-mobile-submenu">
                    <Link to="/gallery/photo" className="vj-mobile-sublink">Photos</Link>
                    <Link to="/gallery/video" className="vj-mobile-sublink">Videos</Link>
                  </div>
                )}
              </div>

              {/* Services Accordion */}
              <div>
                <div className="vj-mobile-link cursor-pointer" onClick={() => toggleAccordion('services')}>
                  <span>Services</span>
                  <ChevronDown size={16} className={activeAccordion === 'services' ? 'rotate-180' : ''} />
                </div>
                {activeAccordion === 'services' && (
                  <div className="vj-mobile-submenu">
                    <Link to="/services/accomodation" className="vj-mobile-sublink">Accomodation</Link>
                    <a
                      href="https://adminoffice.vidyajyoticollege.in"
                      target="_blank"
                      rel="noreferrer"
                      className="vj-mobile-sublink d-flex justify-content-between align-items-center"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span>Admin Office</span> <ExternalLink size={12} />
                    </a>
                  </div>
                )}
              </div>

              {/* Publications Accordion */}
              <div>
                <div className="vj-mobile-link cursor-pointer" onClick={() => toggleAccordion('publications')}>
                  <span>Publications</span>
                  <ChevronDown size={16} className={activeAccordion === 'publications' ? 'rotate-180' : ''} />
                </div>
                {activeAccordion === 'publications' && (
                  <div className="vj-mobile-submenu">
                    <Link to="/publications/youtube" className="vj-mobile-sublink">YouTube Channel</Link>
                    <Link to="/publications/vj-journal" className="vj-mobile-sublink">Vidyajyoti Journal (VJTR)</Link>
                    <Link to="/publications/ace" className="vj-mobile-sublink">AVE Reflections</Link>
                    <Link to="/publications/vjtoday" className="vj-mobile-sublink">VJ Today</Link>
                  </div>
                )}
              </div>

              <Link to="/academics/depth" className="vj-mobile-link">DEPTh</Link>
              <Link to="/contact" className="vj-mobile-link">Contact</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
